import { formatStockMoney } from "../../../../utility/stockValuation";
import "./StockModal.scss";
import FormField from "../../../../components/formField/FormField";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "react-alert";
import { useSelector } from "react-redux";
import * as yup from "yup";
import StockModal from "./StockModal";
import restockStockItem from "../../../../utility/restockStockItem";
import { formatQuantity, isDecimalInput } from "../../../../utility/inputValidation";

const DEFAULT_FORM_DATA = {
  quantity: "",

};

const ProductLine = ({ label, value }) => (
  <div className="product-line">
    <p className="product-line-label">{label}</p>
    <p className="product-line-value">{value || "-"}</p>
  </div>
);

export default function RestockModal({ open, onClose, onCreated, product, venueId }) {
  const { t } = useTranslation("stockManagement");
  const alert = useAlert();
  const userStore = useSelector((state) => state.userReducer);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const pendingRestock = useRef(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const validationSchema = useMemo(
    () =>
      yup.object({
        quantity: yup
          .number()
          .typeError(t("verification_restock_quantity_number"))
          .required(t("verification_restock_quantity_required"))
          .moreThan(0, t("verification_restock_quantity_min")),

      }),
    [t],
  );

  const submitHandler = async () => {
    try {
      setIsSubmitting(true);
      setErrors({});

      const payload = {
        quantity: formData.quantity === "" ? null : Number(formData.quantity),

      };
      await validationSchema.validate(payload, { abortEarly: false });
      const key = `${product?.id}:${payload.quantity}`;
      if (pendingRestock.current && pendingRestock.current.key !== key) throw new Error('RETRY_PENDING_OPERATION');
      pendingRestock.current ||= { key, id: `${Date.now()}-${Math.random().toString(36).slice(2)}` };
      await restockStockItem({
        stockItemId: product?.id,
        quantity: payload.quantity,
        operationId: pendingRestock.current.id,
        venueId,
        createdBy: userStore?.users?.uid,
      });

      pendingRestock.current = null;
      setFormData(DEFAULT_FORM_DATA);
      await onCreated();
    } catch (err) {
      if (err?.name === "ValidationError") {
        const formattedErrors = {};
        err.inner.forEach((error) => (formattedErrors[error.path] = error.message));
        setErrors(formattedErrors);
      } else {
        alert.show(t("restock_modal_submit_error"), { type: "error" });
        console.error("Error restocking item:", err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateHandler = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const closeModal = () => {
    if (isSubmitting) return;
    setFormData(DEFAULT_FORM_DATA);
    setErrors({});
    onClose();
  };

  return (
    <StockModal
      open={open}
      onClose={closeModal}
      onConfirm={submitHandler}
      isLoading={isSubmitting}
      title={t("restock_modal_title")}
    >
      <div className="stock-action-modal-products">
        <p className="stock-action-modal-products-subtitle">{t("restock_modal_subtitle")}</p>
        <ProductLine label={t("restock_modal_line_id")} value={product?.id} />
        <ProductLine label={t("restock_modal_line_stock_item")} value={product?.name} />
        <ProductLine
          label={t("restock_modal_line_on_hand")}
          value={formatQuantity(product?.onHand)}
        />
        <ProductLine label={t("restock_modal_line_unit")} value={product?.unit} />
        <ProductLine label={t("stock_unit_cost")} value={formatStockMoney(product?.unitCost)} />
        <FormField
          value={formData.quantity}
          error={errors.quantity}
          onChange={(value) => {
            if (isDecimalInput(value)) updateHandler("quantity", value);
          }}
          label={t("restock_modal_quantity_to_add")}
        />
        <ProductLine label={t("stock_movement_value")} value={formatStockMoney(
          product?.unitCost == null || !formData.quantity ? null : Math.round(Number(formData.quantity) * product.unitCost * 100) / 100
        )} />
      </div>
    </StockModal>
  );
}
